import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { FaMapMarkerAlt, FaArrowRight, FaShieldAlt, FaCheckCircle, FaExclamationTriangle, FaSpinner } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, delay: d },
});

export default function EnterZip() {
  const navigate = useNavigate();
  const [zip, setZip] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | checking | valid | invalid
  const [place, setPlace] = useState(null); // { city, state, abbr }
  const cacheRef = useRef({});
  const abortRef = useRef(null);

  // Prefill from existing localStorage
  useEffect(() => {
    const existing = localStorage.getItem("userZipCode");
    if (existing) setZip(existing);
  }, []);

  // Debounced validation when ZIP looks complete
  useEffect(() => {
    setError("");
    setPlace(null);
    setStatus("idle");

    const clean = (zip || "").trim();
    if (!/^\d{5}$/.test(clean)) return; // only validate 5-digit

    const t = setTimeout(() => {
      void checkZip(clean);
    }, 300);
    return () => clearTimeout(t);
  }, [zip]);

  async function checkZip(z) {
    const code = (z || zip || "").trim();
    if (!/^\d{5}$/.test(code)) return;

    // cache
    if (cacheRef.current[code]) {
      setPlace(cacheRef.current[code]);
      setStatus("valid");
      setError("");
      return;
    }

    // abort any in‑flight
    try {
      abortRef.current?.abort();
    } catch {}
    abortRef.current = new AbortController();

    setStatus("checking");
    setError("");

    try {
      const res = await fetch(`https://api.zippopotam.us/us/${code}`, {
        signal: abortRef.current.signal,
      });
      if (!res.ok) throw new Error("ZIP not found");
      const data = await res.json();
      const first = data?.places?.[0];
      if (!first) throw new Error("ZIP not found");
      const info = {
        city: first["place name"],
        state: first["state"],
        abbr: first["state abbreviation"],
      };
      cacheRef.current[code] = info;
      setPlace(info);
      setStatus("valid");
    } catch (e) {
      if (e?.name === "AbortError") return; // ignore aborted
      setPlace(null);
      setStatus("invalid");
      setError("We couldn't verify that ZIP. Please check and try again.");
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    const clean = (zip || "").trim();
    if (!/^\d{5}$/.test(clean)) {
      setStatus("invalid");
      setError("Please enter a valid 5‑digit ZIP.");
      return;
    }
    if (status !== "valid") {
      await checkZip(clean);
      if (status !== "valid" && !cacheRef.current[clean]) return; // still not valid
    }
    // Save + continue
    localStorage.setItem("userZipCode", clean);
    navigate("/plans");
  }

  return (
    <div className="bg-white text-[#002b5c]">
      <Helmet>
        <title>Start — Enter ZIP | Your Choice Associates</title>
        <meta name="description" content="Enter your ZIP to find a local licensed broker and browse Medicare & Life Insurance options." />
        <link rel="canonical" href="https://insurancebyag.com/start" />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#f7fbff] to-white">
        <div className="mx-auto max-w-5xl px-6 md:px-8 py-16">
          <motion.div {...fadeUp(0)} className="text-center">
            <span className="inline-block rounded-full bg-[#e8f1fb] px-3 py-1 text-xs font-semibold tracking-wider text-[#002b5c]">Get started</span>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">Enter your ZIP code</h1>
            <p className="mx-auto mt-3 max-w-2xl text-gray-700">We’ll match you with a local licensed broker and guide you to the right plan.</p>
          </motion.div>

          {/* ZIP form */}
          <motion.form {...fadeUp(0.05)} onSubmit={onSubmit} className="mx-auto mt-8 max-w-md rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
            {error && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">{error}</div>
            )}
            <label htmlFor="zip" className="text-sm font-medium">ZIP code</label>
            <div className={`mt-1 flex items-center rounded-2xl border ${status === "invalid" ? "border-red-300" : "border-gray-300"} bg-white pl-3 focus-within:ring-2 focus-within:ring-[#0B5EFD]/30`}>
              <FaMapMarkerAlt className="text-[#e31c3d]" aria-hidden />
              <input
                id="zip"
                value={zip}
                onChange={(e) => { const v = (e.target.value || '').replace(/\D/g, '').slice(0, 5); setZip(v); }}
                className="w-full rounded-2xl border-0 bg-transparent px-3 py-3 outline-none"
                placeholder="e.g. 11201"
                autoComplete="postal-code"
                inputMode="numeric"
                maxLength={5}
                autoFocus
                aria-describedby="zip-help zip-status"
              />
              <div className="pr-3">
                {status === "checking" && <FaSpinner className="animate-spin" aria-label="Checking" />}
                {status === "valid" && <FaCheckCircle className="text-green-600" aria-label="Valid ZIP" />}
                {status === "invalid" && <FaExclamationTriangle className="text-red-500" aria-label="Invalid ZIP" />}
              </div>
            </div>

            {/* Location preview */}
            <div id="zip-status" className="mt-2 min-h-[1.25rem] text-sm" aria-live="polite">
              {status === "checking" && <span className="text-gray-600">Checking ZIP…</span>}
              {status === "valid" && place && (
                <span className="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1 text-green-800">
                  <FaCheckCircle /> {place.city}, {place.abbr}
                </span>
              )}
              {status === "invalid" && <span className="text-red-700">ZIP not found. Please check and try again.</span>}
            </div>

            <button
              type="submit"
              disabled={!/^\d{5}$/.test(zip) || status === "checking" || status === "invalid"}
              className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-[#002b5c] px-4 py-3 text-sm font-semibold text-white shadow-sm transition enabled:hover:bg-[#001b35] disabled:opacity-60"
            >
              Continue <FaArrowRight className="ml-2" />
            </button>
            <p id="zip-help" className="mt-3 text-center text-xs text-gray-600">We’ll only use your ZIP to connect you with nearby brokers.</p>
          </motion.form>

          {/* Trust bar */}
          <motion.div {...fadeUp(0.1)} className="mx-auto mt-6 max-w-md text-center text-xs text-gray-600">
            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1">
              <FaShieldAlt aria-hidden /> Secure & private
            </div>
          </motion.div>
        </div>
      </section>
 
      <Footer />
    </div>
  );
}
